// One-time import of an existing login from the legacy app origin
// (app.gitcha.com) into the app hosted under www.gitcha.com/feed. The session
// lives in localStorage, which is per origin, so without this every signed-in
// user would be signed out at cutover. The legacy origin serves
// public/session-bridge.html; this loads it in a hidden iframe and asks for the
// session over postMessage (protocol documented in public/session-bridge.js).
//
// Off unless REACT_APP_SESSION_BRIDGE_URL is set. Runs at most once per browser
// (gcSessionImportTried), never when already signed in, never from inside a
// frame, and never blocks boot for longer than timeoutMs.
import { USER_INFO_KEY } from "../constants/common";

// "done" (or the original "1") once the bridge gave a definite answer; otherwise
// "attempts:N", the number of tries so far that ended in a timeout or error.
export const IMPORT_TRIED_KEY = "gcSessionImportTried";
export const IMPORT_DONE = "done";
export const MAX_IMPORT_ATTEMPTS = 3;
export const BRIDGE_TIMEOUT_MS = 1500;
const ACK_GRACE_MS = 500;

const REQUEST = "gitcha:session-export:request";
const RESPONSE = "gitcha:session-export:response";
const ACK = "gitcha:session-export:ack";

const parseBridgeUrl = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" ? parsed : null;
  } catch {
    return null;
  }
};

const isFramed = (win) => {
  try {
    return win.self !== win.top;
  } catch {
    return true; // cross-origin top: certainly framed
  }
};

// Returns why an import must not run, or null when it should.
const ATTEMPTS_PREFIX = "attempts:";
const attemptsMade = (value) => {
  if (!value) return 0;
  if (!value.startsWith(ATTEMPTS_PREFIX)) return MAX_IMPORT_ATTEMPTS; // "1"/"done": final
  const n = Number(value.slice(ATTEMPTS_PREFIX.length));
  return Number.isInteger(n) && n >= 0 ? n : MAX_IMPORT_ATTEMPTS;
};

export const skipReason = ({ bridgeUrl, win = window, storage }) => {
  if (!bridgeUrl) return "disabled";
  const bridge = parseBridgeUrl(bridgeUrl);
  if (!bridge) return "invalid-bridge-url";
  if (bridge.origin === win.location.origin) return "same-origin";
  if (isFramed(win)) return "framed";
  try {
    // Read inside the try: merely touching localStorage throws when blocked.
    const store = storage || win.localStorage;
    if (store.getItem(USER_INFO_KEY)) return "has-session";
    if (attemptsMade(store.getItem(IMPORT_TRIED_KEY)) >= MAX_IMPORT_ATTEMPTS)
      return "already-tried";
  } catch {
    return "storage-unavailable";
  }
  return null;
};

// deps: { decodeJwt(token) -> payload (throws if malformed),
//         isImpersonated(accessToken) -> boolean, now() -> ms }
export const isImportableSession = (
  raw,
  { decodeJwt, isImpersonated, now = Date.now },
) => {
  if (typeof raw !== "string") return false;
  let session;
  try {
    session = JSON.parse(raw);
  } catch {
    return false;
  }
  const accessToken = session?.token?.accessToken;
  const refreshToken = session?.token?.refreshToken;
  if (!session?.userInfo || typeof session.userInfo !== "object") return false;
  if (typeof accessToken !== "string" || typeof refreshToken !== "string")
    return false;
  // An admin impersonation session must never move between origins.
  if (isImpersonated(accessToken)) return false;
  try {
    // The access token is refreshed on demand; only a dead refresh token makes
    // the session worthless.
    return decodeJwt(refreshToken).exp * 1000 > now();
  } catch {
    return false;
  }
};

const newRequestId = (win) =>
  win.crypto?.randomUUID
    ? win.crypto.randomUUID()
    : `r${Date.now()}${Math.random().toString(36).slice(2)}`;

// Resolves with a short outcome string (for logging/tests); never rejects.
export const importSession = ({
  bridgeUrl,
  deps,
  win = window,
  storage,
  timeoutMs = BRIDGE_TIMEOUT_MS,
}) => {
  const reason = skipReason({ bridgeUrl, win, storage });
  if (reason) return Promise.resolve(reason);
  let store;
  try {
    store = storage || win.localStorage;
    // Counted before asking, so a crash or a slow bridge costs a bounded number
    // of attempts per browser instead of a delay on every visit.
    store.setItem(
      IMPORT_TRIED_KEY,
      `${ATTEMPTS_PREFIX}${attemptsMade(store.getItem(IMPORT_TRIED_KEY)) + 1}`,
    );
  } catch {
    return Promise.resolve("storage-unavailable");
  }
  // A definite answer from the bridge (session, none, rejected) is final; a
  // timeout or error is not, so a slow first visit gets another try.
  const markDone = () => {
    try {
      store.setItem(IMPORT_TRIED_KEY, IMPORT_DONE);
    } catch {
      /* the attempt counter still bounds retries */
    }
  };

  const bridge = parseBridgeUrl(bridgeUrl);
  const requestId = newRequestId(win);
  const iframe = win.document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.tabIndex = -1;
  iframe.style.display = "none";

  return new Promise((resolve) => {
    let settled = false;
    let timer;
    const finish = (outcome, { ack = false } = {}) => {
      if (settled) return;
      settled = true;
      win.clearTimeout(timer);
      win.removeEventListener("message", onMessage);
      if (ack) {
        // The ack must reach the bridge before its frame is torn down.
        win.setTimeout(() => iframe.remove(), ACK_GRACE_MS);
      } else {
        iframe.remove();
      }
      resolve(outcome);
    };

    function onMessage(event) {
      if (event.origin !== bridge.origin) return;
      if (event.source !== iframe.contentWindow) return;
      const data = event.data;
      if (!data || data.type !== RESPONSE || data.requestId !== requestId)
        return;
      markDone();
      if (data.userInfo == null) return finish("none");
      if (!isImportableSession(data.userInfo, deps)) return finish("rejected");
      try {
        store.setItem(USER_INFO_KEY, data.userInfo);
      } catch {
        return finish("storage-unavailable");
      }
      iframe.contentWindow.postMessage({ type: ACK, requestId }, bridge.origin);
      finish("imported", { ack: true });
    }

    win.addEventListener("message", onMessage);
    timer = win.setTimeout(() => finish("timeout"), timeoutMs);
    iframe.onload = () => {
      iframe.contentWindow.postMessage(
        { type: REQUEST, requestId },
        bridge.origin,
      );
    };
    iframe.onerror = () => finish("error");
    iframe.src = bridge.href;
    win.document.body.appendChild(iframe);
  });
};

// Runs the import, reports its outcome, then starts the app. Nothing in the
// import or in reporting may keep the app from starting.
export const startAfterImport = async ({ options, track, start }) => {
  try {
    const outcome = await importSession(options);
    try {
      track(outcome);
    } catch {
      /* analytics must never block boot */
    }
  } catch {
    /* import never rejects, but be safe */
  }
  start();
};
